/* eslint-disable import/prefer-default-export */
import React, { useMemo } from 'react';
import { map } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Skeleton } from 'antd';
import {
  calcPercent,
  dateArray,
  formatDayWithUtc,
  getAccessCellValues,
  isWeekend,
  queryToURL,
} from '@helpers/index';
import { useSetDate } from '@hooks/index';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  AllViewRow,
  AreaSectionStaffingTableProps,
  StaffingTableRowProps,
} from './types';
import '@styles/staffingTable.less';

dayjs.extend(customParseFormat);

const buildAccessUrl = (day: number) => ({
  pathname: '/workers-accesses',
  search: queryToURL({
    source: 'contractor_worker',
    type: 'entry',
    result: 'authorized',
    period: formatDayWithUtc(day, 'YYYY-MM-DD'),
    tab: 'accesses',
  }),
});

const DataCells = ({ dates, getValues }: Pick<StaffingTableRowProps, 'dates' | 'getValues'>) => (
  <>
    {map(dates, (date) => (
      <td
        className={`diagonal-cell clickable ${isWeekend(date) ? 'weekend' : ''}`}
        key={date}
      >
        <Link className="absolute inset-0 text-current" to={buildAccessUrl(date)}>
          <span className="relative">{getValues(date).real}</span>
          <span className="total">{getValues(date).expected}</span>
        </Link>
      </td>
    ))}
    <td className="diagonal-cell fixed-right-column">
      <span className="relative-right-column">{getValues().real}</span>
      <span className="total">{getValues().expected}</span>
    </td>
  </>
);

const PercentCells = ({ dates, getValues }: Pick<StaffingTableRowProps, 'dates' | 'getValues'>) => (
  <>
    {map(dates, (date) => (
      <td className={`total-cell ${isWeekend(date) ? 'weekend' : ''}`} key={date}>
        <span>{`${calcPercent(getValues(date))}%`}</span>
      </td>
    ))}
    <td className="total-cell fixed-right-column">
      <span>{`${calcPercent(getValues())}%`}</span>
    </td>
  </>
);

const ContractorRow = ({
  label,
  dates,
  getValues,
  isAllView,
}: StaffingTableRowProps) => (
  <tr>
    <th className={`fixed-column clickable diagonal-cell-hh ${isAllView ? 'normal' : ''}`}>
      {label}
    </th>
    {!isAllView && <td>&nbsp;</td>}
    <DataCells dates={dates} getValues={getValues} />
    {!isAllView && <td className="hidden-right-column" />}
  </tr>
);

const PercentRow = ({
  label,
  dates,
  getValues,
  isAllView,
}: StaffingTableRowProps) => (
  <tr className={isAllView ? 'area-section-percent-row' : undefined}>
    <th className={`fixed-column clickable diagonal-cell-hh ${isAllView ? 'normal' : ''}`}>
      {label}
    </th>
    {!isAllView && <td>&nbsp;</td>}
    <PercentCells dates={dates} getValues={getValues} />
    {!isAllView && <td className="hidden-right-column" />}
  </tr>
);

const getConsolidatedValues = (rows: AllViewRow[], day?: number) => rows.reduce(
  (acc, { access }) => {
    const values = getAccessCellValues(access, day);
    return {
      expected: acc.expected + values.expected,
      real: acc.real + values.real,
      incomplete: acc.incomplete + values.incomplete,
    };
  },
  { expected: 0, real: 0, incomplete: 0 },
);

const useStaffingTableDates = (
  access: AreaSectionStaffingTableProps['access'],
  since?: string,
  until?: string,
) => {
  const { dates: accessDates } = useSetDate(access);

  const rangeDates = useMemo(() => {
    if (!since || !until) return [];
    const from = dayjs(since, 'DD-MM-YYYY').format('YYYY-MM-DD');
    const to = dayjs(until, 'DD-MM-YYYY').format('YYYY-MM-DD');
    return dateArray(from, to);
  }, [since, until]);

  return accessDates.length > 0 ? accessDates : rangeDates;
};

const AreaSectionStaffingTable = ({
  access,
  loading,
  isAllView,
  contractors = [],
  contractorsAccess = [],
  since,
  until,
}: AreaSectionStaffingTableProps) => {
  const dates = useStaffingTableDates(access, since, until);
  const { t } = useTranslation();
  const tableClassName = isAllView
    ? 'fixed-column-table ks-table-bordered area-section-staffing-table--all'
    : 'fixed-column-table ks-table-bordered';

  const allViewRows = useMemo(() => {
    const accessByContractorId = new Map(
      contractorsAccess.map((row) => [row.id, row.access]),
    );

    return contractors.map((contractor) => ({
      id: contractor.id,
      name: contractor.name,
      access: accessByContractorId.get(contractor.id),
    }));
  }, [contractors, contractorsAccess]);

  const displayDateHeader = map(dates, (date) => (
    <th
      key={date}
      className={`diagonal-cell-hv ${isWeekend(date) ? 'weekend' : ''}`}
    >
      {formatDayWithUtc(date, 'dd DD/MM')}
    </th>
  ));

  return (
    <Skeleton loading={loading}>
      <div className="fixed-table-wrapper">
        <div className={tableClassName}>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th className="fixed-column fixed-column-header">&nbsp;</th>
                {!isAllView && <th className="hidden-column">&nbsp;</th>}
                {displayDateHeader}
                <th className="fixed-right-column fixed-right-column-header">
                  {t('workerAccess.total')}
                </th>
                {!isAllView && <th className="hidden-right-column">&nbsp;</th>}
              </tr>
            </thead>
            <tbody>
              {isAllView ? (
                <>
                  {map(allViewRows, ({ id, name, access: contractorAccess }, index) => (
                    <React.Fragment key={id}>
                      {index > 0 && (
                        <tr className="area-section-contractor-separator" aria-hidden="true">
                          <td colSpan={dates.length + 2} />
                        </tr>
                      )}
                      <ContractorRow
                        label={name}
                        dates={dates}
                        getValues={(day) => getAccessCellValues(contractorAccess, day)}
                        isAllView
                      />
                      <PercentRow
                        label={t('workerAccess.totalAccess')}
                        dates={dates}
                        getValues={(day) => getAccessCellValues(contractorAccess, day)}
                        isAllView
                      />
                    </React.Fragment>
                  ))}
                  {allViewRows.length > 0 && (
                    <tr className="area-section-contractor-separator" aria-hidden="true">
                      <td colSpan={dates.length + 2} />
                    </tr>
                  )}
                  <ContractorRow
                    label={t('workerAccess.total')}
                    dates={dates}
                    getValues={(day) => getConsolidatedValues(allViewRows, day)}
                    isAllView
                  />
                  <PercentRow
                    label={t('workerAccess.totalAccess')}
                    dates={dates}
                    getValues={(day) => getConsolidatedValues(allViewRows, day)}
                    isAllView
                  />
                </>
              ) : (
                <>
                  <ContractorRow
                    label={t('workerAccess.total')}
                    dates={dates}
                    getValues={(day) => getAccessCellValues(access, day)}
                    isAllView={false}
                  />
                  <PercentRow
                    label={t('workerAccess.totalAccess')}
                    dates={dates}
                    getValues={(day) => getAccessCellValues(access, day)}
                    isAllView={false}
                  />
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Skeleton>
  );
};

export default AreaSectionStaffingTable;
